import React from 'react';
import styled, { css } from 'styled-components';
import { Classable, HasChildren, Shapeable, Organisation, Course, Program, FeedEntry, Post, Video, Event } from '@shapeable/types';
import { breakpoints, theme } from '@shapeable/theme';
import { classNames } from '@shapeable/utils';
import { CellLabel, LinkButton, OrganisationLogoCard, useLang } from '@shapeable/ui';
import { Link } from '@shapeable/villars-types';

const cls = classNames('entity-curator-foot-meta');

// -------- Types -------->

export type CuratorEntity = Course | Program | FeedEntry | Post | Video | Event | Link;

// -------- Props -------->

export type EntityCuratorFootMetaProps = Classable & HasChildren & { 
  entity?: CuratorEntity;
};

export const EntityCuratorFootMetaDefaultProps: Omit<EntityCuratorFootMetaProps, 'entity'> = {
};

// -------- Child Component Props -------->

type ContainerProps = {

};

// -------- Styles -------->

const ContainerStyles = breakpoints({
  base: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.UNIT(5)};
  `,
});

const PartnersStyles = breakpoints({
  base: css`
    display: flex;
    align-items: center;
    gap: ${theme.UNIT(2)};
  `,
});

const LabelStyles = breakpoints({
  base: css`
    
  `,
});

const ButtonStyles = breakpoints({
  base: css`
    max-width: 180px;
  `,
});

const OrganisationLogoCardStyles = breakpoints({
  base: css`
    flex-grow: 0;
    padding: ${theme.UNIT(2)};
  `,
});

// -------- Components -------->

const My = {
  Container: styled.div<ContainerProps>`${ContainerStyles}`,
    Partners: styled.div`${PartnersStyles}`,
      Label: styled(CellLabel)`${LabelStyles}`,
      OrganisationLogoCard: styled(OrganisationLogoCard)`${OrganisationLogoCardStyles}`,
    DiscoverButton: styled(LinkButton)`${ButtonStyles}`,
};

// -------- Helper Functions -------->

const getOrganisation = (entity: any): Organisation | undefined => {
  // Check for providers field first (Course, Program)
  if (entity?.providers?.[0]) {
    return entity.providers[0] as Organisation;
  }
  if (entity?.supportingOrganisations?.[0]) {
    return entity.supportingOrganisations[0] as Organisation;
  }
  // Check for organisations field (FeedEntry, Post, Video)
  if (entity?.organisations?.[0]) {
    return entity.organisations[0] as Organisation;
  }
  if (entity?.organisationRoles?.[0]?.organisation) {
    return entity.organisationRoles[0].organisation as Organisation;
  }
  return undefined;
};

const getDiscoverMoreUrl = (entity: any): string | undefined => {
  // Check for links array with link.url structure
  if (entity?.links?.[0]?.url) {
    return entity.links[0].url;
  }
  // Check for direct url field
  if (entity?.url) {
    return entity.url;
  }
  // Fallback to entity path
  if (entity?.path) {
    return entity.path;
  }
  return undefined;
};

export const EntityCuratorFootMeta: Shapeable.FC<EntityCuratorFootMetaProps> = (props) => {
  const { className, children, entity } = props;

  const organisation = getOrganisation(entity);
  const discoverMoreUrl = getDiscoverMoreUrl(entity);

  const t = useLang();
  
  return (
    <My.Container className={cls.name(className)}>
      {
        !!organisation && (
          <My.Partners>
            <My.Label>{t('Partners:')}</My.Label>
            <My.OrganisationLogoCard 
              colors={{ background: 'transparent' }} 
              entity={organisation} 
              size={40} 
            />
          </My.Partners>
        )
      }
      {
        !!discoverMoreUrl && (
          <My.DiscoverButton href={discoverMoreUrl}>
            {t('Discover More')}
          </My.DiscoverButton>
        )
      }
      {children}
    </My.Container>
  )
};

EntityCuratorFootMeta.cls = cls;